import React from 'react';
import {
    getActivityLabel,
    getActivityMeta,
    isActivityItem,
    resolveActivityType,
} from '../activityTypes.js';
import { getBadItemLabel, getBadItemSubtitle } from '../badItemTypes.js';
import { GAME, ITEM_TYPES } from '../config.js';
import { getItemSlotStyle } from '../systems/itemPositionStyle.js';
import { ActivityIcon } from './ActivityIcon.jsx';

function formatPoints(points) {
    return points > 0 ? `+${points}` : String(points);
}

function itemCardStyle(item, popping = false) {
    if (popping) {
        return {
            '--lp-item-life': '0s',
            '--lp-item-rotate': `${item.rotate ?? 0}deg`,
        };
    }

    return {
        '--lp-item-life': `${Math.max(0.8, item.lifetimeMs / 1000)}s`,
        '--lp-item-rotate': `${item.rotate ?? 0}deg`,
    };
}

function ItemSlot({ item, boardSize, children }) {
    return (
        <div className="lp-game__item-slot" style={getItemSlotStyle(item, boardSize)}>
            {children}
        </div>
    );
}

function ActivityFloatingItem({ item, label, boardSize, onItemClick, popping = false }) {
    const activityType = resolveActivityType(item);
    const meta = getActivityMeta(item);
    const className = [
        'lp-game__item',
        'lp-game__item--activity',
        `lp-game__item--activity-${meta.className}`,
        popping && 'lp-game__item--popping',
    ]
        .filter(Boolean)
        .join(' ');

    const tagProps = popping
        ? { type: 'button', disabled: true, 'aria-hidden': true }
        : {
              type: 'button',
              'aria-label': `${label} ${formatPoints(item.points)}`,
              onPointerDown: (event) => {
                  event.preventDefault();
                  onItemClick(item.id);
              },
          };

    return (
        <ItemSlot item={item} boardSize={boardSize}>
            <button
                type="button"
                className={className}
                style={itemCardStyle(item, popping)}
                {...tagProps}
            >
                <span className="lp-game__activity-icon-wrap">
                    <span className="lp-game__activity-icon">
                        <ActivityIcon type={activityType} />
                    </span>
                </span>

                <span className="lp-game__activity-label">{label}</span>
            </button>
        </ItemSlot>
    );
}

function resolveLeadLabel(item, t) {
    if (item.labelKey && typeof t === 'function') {
        return t(item.labelKey, item.label);
    }
    return item.label;
}

function resolveLeadSubtitle(item, t) {
    if (item.subtitleKey && typeof t === 'function') {
        return t(item.subtitleKey, item.subtitle);
    }
    return item.subtitle;
}

function BackendLeadFloatingItem({ item, boardSize, onItemClick, t, popping = false }) {
    const label = resolveLeadLabel(item, t);
    const detailLine = String(item.detailLine || '').trim();
    const metaLine = String(item.metaLine || '').trim();
    const badgeLabel = typeof t === 'function' ? t('items.backendLead', 'Lead') : 'Lead';

    const className = [
        'lp-game__item',
        'lp-game__item--backend',
        'lp-game__item--backend-stream',
        popping && 'lp-game__item--popping',
    ]
        .filter(Boolean)
        .join(' ');

    const tagProps = popping
        ? { type: 'button', disabled: true, 'aria-hidden': true }
        : {
              type: 'button',
              'aria-label': [label, detailLine, metaLine].filter(Boolean).join(' '),
              onPointerDown: (event) => {
                  event.preventDefault();
                  onItemClick(item.id);
              },
          };

    const wrapStyle = itemCardStyle(item, popping);

    return (
        <ItemSlot item={item} boardSize={boardSize}>
            <div className="lp-game__backend-wrap" style={wrapStyle}>
                <span className="lp-game__backend-badge">{badgeLabel}</span>
                <button type="button" className={className} {...tagProps}>
                    <span className="lp-game__backend-shine" aria-hidden />
                    <span className="lp-game__item-icon lp-game__item-icon--backend">
                        {item.icon}
                    </span>
                    <span className="lp-game__item-content lp-game__item-content--backend">
                        <span className="lp-game__item-label">{label}</span>
                        {detailLine ? (
                            <span className="lp-game__item-subtitle">{detailLine}</span>
                        ) : null}
                        {!detailLine && metaLine ? (
                            <span className="lp-game__item-subtitle">{metaLine}</span>
                        ) : null}
                        {detailLine && metaLine ? (
                            <span className="lp-game__backend-meta">{metaLine}</span>
                        ) : null}
                    </span>
                </button>
            </div>
        </ItemSlot>
    );
}

function LeadFloatingItem({ item, boardSize, onItemClick, t, popping = false }) {
    const isBad = item.type === ITEM_TYPES.BAD_ITEM;
    const label = isBad ? getBadItemLabel(item, t) : resolveLeadLabel(item, t);
    const subtitle = isBad ? getBadItemSubtitle(item, t) : resolveLeadSubtitle(item, t);
    const isHot = item.type === ITEM_TYPES.HOT_LEAD;
    const isBonus = item.type === ITEM_TYPES.BONUS;
    const isFromBackend = Boolean(item.fromBackend);

    const className = [
        'lp-game__item',
        isBad && 'lp-game__item--bad',
        isHot && 'lp-game__item--hot',
        isBonus && 'lp-game__item--bonus',
        isFromBackend && 'lp-game__item--backend',
        popping && 'lp-game__item--popping',
    ]
        .filter(Boolean)
        .join(' ');

    const tagProps = popping
        ? { type: 'button', disabled: true, 'aria-hidden': true }
        : {
              type: 'button',
              'aria-label': `${label} ${formatPoints(item.points)}`,
              onPointerDown: (event) => {
                  event.preventDefault();
                  onItemClick(item.id);
              },
          };

    return (
        <ItemSlot item={item} boardSize={boardSize}>
            <button
                type="button"
                className={className}
                style={itemCardStyle(item, popping)}
                {...tagProps}
            >
                <span className="lp-game__item-icon">{item.icon}</span>
                <span className="lp-game__item-content">
                    <span className="lp-game__item-label">{label}</span>
                    <span className="lp-game__item-subtitle">{subtitle}</span>
                </span>
            </button>
        </ItemSlot>
    );
}

function FloatingItemComponent({ item, boardSize, onItemClick, t }) {
    const isBadItem = item.type === ITEM_TYPES.BAD_ITEM;
    const popping = Boolean(item.popping);

    if (GAME.ACTIVITIES_ONLY && !isActivityItem(item) && !isBadItem && !item.fromBackend) {
        return null;
    }

    if (isActivityItem(item)) {
        return (
            <ActivityFloatingItem
                item={item}
                label={getActivityLabel(item, t)}
                boardSize={boardSize}
                onItemClick={onItemClick}
                popping={popping}
            />
        );
    }

    if (item.fromBackend) {
        return (
            <BackendLeadFloatingItem
                item={item}
                boardSize={boardSize}
                onItemClick={onItemClick}
                t={t}
                popping={popping}
            />
        );
    }

    return (
        <LeadFloatingItem
            item={item}
            boardSize={boardSize}
            onItemClick={onItemClick}
            t={t}
            popping={popping}
        />
    );
}

export const FloatingItem = FloatingItemComponent;

export { isActivityItem, getActivityLabel, getActivityMeta } from '../activityTypes.js';
